import { z } from 'zod';
import { FILE_NAME_MAX, MESSAGE_CONTENT_MAX } from '../constants.js';
import { idSchema, isoTimestampSchema } from './primitives.js';
/**
 * `text` is an ordinary chat message.
 *
 * `file-share-event` is a shared file. `content` is its name and `attachment`
 * says how to fetch it. The bytes themselves never enter the database: they are
 * held in server memory until every recipient has downloaded them or they
 * expire - see `FileStore` on the server.
 */
export const messageTypeSchema = z.enum(['text', 'file-share-event']);
/** Everyone who reacted with one emoji. One reaction per person per message. */
export const reactionSchema = z.object({
    emoji: z.string().min(1).max(16),
    userIds: z.array(idSchema).min(1),
});
/** Enough of the replied-to message to draw the quote above a reply. */
export const messageReplySchema = z.object({
    id: idSchema,
    senderId: idSchema,
    content: z.string(),
    type: messageTypeSchema,
});
/** A shared file: metadata only. Download it from `GET /api/v1/files/:fileId`. */
export const attachmentSchema = z.object({
    fileId: idSchema,
    name: z.string().min(1).max(FILE_NAME_MAX),
    size: z.number().int().nonnegative(),
    mimeType: z.string().max(255),
    /** After this the server has dropped the bytes, whether or not anyone downloaded them. */
    expiresAt: isoTimestampSchema,
});
export const messageSchema = z.object({
    id: idSchema,
    channelId: idSchema,
    senderId: idSchema,
    content: z.string().min(1).max(MESSAGE_CONTENT_MAX),
    type: messageTypeSchema,
    createdAt: isoTimestampSchema,
    /** When the sender last changed `content`; `null` for a message never edited. */
    editedAt: isoTimestampSchema.nullable(),
    reactions: z.array(reactionSchema),
    /** The message this one answers; `null` if none, or if that message was deleted. */
    replyTo: messageReplySchema.nullable(),
    /** Who originally wrote a forwarded message; `null` for an original. */
    forwardedFrom: idSchema.nullable(),
    /** Present exactly when `type` is `file-share-event`. */
    attachment: attachmentSchema.nullable(),
});
/** Enough of a message for a conversation-list row. */
export const messagePreviewSchema = messageSchema.pick({
    id: true,
    senderId: true,
    content: true,
    type: true,
    createdAt: true,
});
/**
 * What a client emits on `chat.message`. The server assigns `id`, `createdAt`
 * and `senderId` - a client cannot claim to be someone else, so `senderId` is
 * taken from the authenticated socket, never from this payload.
 *
 * The schema bounds `content` by the hard ceiling only. The length actually
 * allowed is the `maxMessageLength` setting, which the server applies on top.
 */
export const sendMessageInputSchema = z.object({
    channelId: idSchema,
    content: z.string().min(1).max(MESSAGE_CONTENT_MAX),
    /** A message in the same channel this one replies to. */
    replyToId: idSchema.optional(),
});
//# sourceMappingURL=message.js.map